import { z } from 'zod';

import { challengeServiceClient } from '@/services/api';
import { ensureAuthTokens } from '@/store/authStore';
import { Challenge, ChallengeSchema } from '@/types/Challenge';
import { UserProfile } from '@/types/UserProfile';
import { FeedItemSchema } from '@/types/FeedItem';
import { PostSchema } from '@/types/Post';

type UnifiedFeedResponse = { items: any[]; user_likes?: string[] };

class ChallengeService {
    // src/services/ChallengeService.ts
    static async getUnifiedFeed({
        limit,
        seen_ids = [],
        post_id = null,
        challenge_id = null,
        q = undefined, // NEW
        q_type = undefined, // NEW: 'tag' | 'key'
    }: {
        limit: number;
        seen_ids?: string[];
        post_id?: string | null;
        challenge_id?: string | null;
        q?: string; // NEW
        q_type?: 'tag' | 'key'; // NEW
    }): Promise<UnifiedFeedResponse> {
        const payload: any = { limit, seen_ids, post_id, challenge_id };
        if (q && q_type) {
            payload.q = q;
            payload.q_type = q_type;
        } // NEW

        console.log('[ChallengeService] getUnifiedFeed payload', payload);

        const resp = await challengeServiceClient.post('/challenge/fetch-unified-feed', payload);
        const raw = resp?.data ?? {};
        const items = Array.isArray(raw) ? raw : (raw.items ?? []);

        const normalized = items.map((it: any) => {
            if (it?.content_type === 'challenge')
                return ChallengeSchema.partial().passthrough().parse(it);
            if (it?.content_type === 'post') return PostSchema.partial().passthrough().parse(it);
            return it;
        });

        return {
            items: normalized,
            user_likes: Array.isArray(raw.user_likes) ? raw.user_likes : [],
        };
    }

    static async getChallenge(challenge_id: string): Promise<Challenge> {
        const resp = await challengeServiceClient.get('/challenge/get-challenge', {
            params: {
                challenge_id,
            },
        });
        return ChallengeSchema.parse(resp.data.challenge);
    }

    static async getUserChallenges(user_id: string, limit: number): Promise<Challenge[]> {
        const resp = await challengeServiceClient.get('/challenge/user-challenges', {
            params: {
                user_id,
                limit,
            },
        });
        console.log('getUserChallenges resp', resp);
        return z.array(ChallengeSchema).parse(resp.data.challenges);
    }

    static async getCountByUserId(user_id: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/user-challenges-count', {
            params: {
                user_id,
            },
        });
        return resp.data.count;
    }

    static async getUserPostCount(user_id: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/user-post-count', {
            params: {
                user_id,
            },
        });
        return resp.data.count;
    }

    static async getUserJudgedChallengesCount(user_id: string, token: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/user-judged-challenge-count', {
            params: {
                user_id,
            },
            headers: {
                // TODO: Remove manual token once interceptors handle this universally
                Authorization: `Bearer ${token}`,
            },
        });
        return resp.data.count;
    }

    static getUserChallengesCount(user_id: string) {
        // Make the GET request using challengeServiceClient
        return challengeServiceClient.get('/challenge/user-challenges-count', {
            params: {
                user_id,
            },
        });
    }

    static async getUserChallengeAuth(challenge_id: string): Promise<Challenge | null> {
        const resp = await challengeServiceClient.get('/challenge/user-challenge', {
            params: {
                challenge_id,
            },
        });
        if (resp.data && resp.data.challenge) {
            return ChallengeSchema.parse(resp.data.challenge);
        }
        return null;
    }

    static getPrizePhotoSignedUrl(
        challenge_id: string,
        token: string,
        challenge_field = 'prize_photo'
    ) {
        return challengeServiceClient.get('/challenge/get-signed-url-for-prize-photo', {
            params: {
                challenge_id,
                challenge_field,
            },
            headers: {
                // TODO: Remove manual token once interceptors handle this universally
                Authorization: `Bearer ${token}`,
            },
        });
    }

    static async updateChallenge(
        accessToken: string,
        challenge: Partial<Challenge>
    ): Promise<boolean> {
        console.log('updateChallenge challenge', challenge);

        // TODO: Remove manual accessToken header once all calls rely on interceptors
        const resp = await challengeServiceClient.post('/challenge/update', challenge, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });

        if (resp.status === 200 && resp.data.challenge) {
            Object.assign(this, resp.data.challenge);
            return true;
        }

        console.error('Failed to update challenge:', resp.data?.message);
        return false;
    }

    // static async updateChallenge(
    //     accessToken: string,
    //     challenge: Partial<Challenge>
    // ): Promise<boolean> {
    //     console.log('updateChallenge challenge', challenge);
    //     try {
    //         const resp = await challengeServiceClient.post('/challenge/update', challenge, {
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 Authorization: `Bearer ${accessToken}`,
    //             },
    //         });
    //         // const resp = await api.updateChallenge(accessToken, this);
    //         if (resp.status === 200 && resp.data.challenge) {
    //             Object.assign(this, resp.data.challenge);
    //             return true;
    //         } else {
    //             console.error('Failed to update challenge:', resp.data.message);
    //             return false;
    //         }
    //     } catch (error) {
    //         console.error('Error updating challenge:', error);
    //         return false;
    //     }
    // }

    static async getRecentChallenges(
        limit: number,
        last_evaluated_key: any = null
    ): Promise<{
        challenges: Challenge[];
        last_evaluated_key: any;
        userProfiles: UserProfile[];
        user_likes: string[];
    }> {
        // Serialize last_evaluated_key if it's not null
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        // Make the GET request using challengeServiceClient
        console.log('[DEBUG] fetching limit =', limit, 'last_evaluated_key =', serializedKey);
        const resp = await challengeServiceClient.get('/challenge/get-recent', {
            params: {
                limit,
                last_evaluated_key: serializedKey,
            },
        });
        console.log('[DEBUG] getRecentChallenges resp', resp);
        if (!resp.data.challenges || resp.data.challenges.length === 0) {
            return { challenges: [], last_evaluated_key: null, userProfiles: [], user_likes: [] };
        }
        return {
            challenges: z.array(ChallengeSchema).parse(resp.data.challenges),
            userProfiles: resp.data.user_profiles,
            last_evaluated_key: resp.data.last_evaluated_key || null,
            user_likes: resp.data.user_likes || [],
        };
    }

    static async getUpcomingChallenges(
        limit: number,
        last_evaluated_key: any = null
    ): Promise<{
        challenges: Challenge[];
        last_evaluated_key: any;
        userProfiles: UserProfile[];
        user_likes: string[];
    }> {
        // Serialize last_evaluated_key if it's not null
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        // Make the GET request using challengeServiceClient
        const resp = await challengeServiceClient.get('/challenge/get-upcoming', {
            params: {
                limit,
                last_evaluated_key: serializedKey,
            },
        });
        if (!resp.data.challenges || resp.data.challenges.length === 0) {
            return { challenges: [], last_evaluated_key: null, userProfiles: [], user_likes: [] };
        }
        return {
            challenges: z.array(ChallengeSchema).parse(resp.data.challenges),
            last_evaluated_key: resp.data.last_evaluated_key || null,
            userProfiles: [],
            user_likes: resp.data.user_likes || [],
        };
    }

    static async getTrendingChallenges(limit: number, last_evaluated_key: any = null) {
        // Serialize last_evaluated_key if it's not null
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        // Make the GET request using challengeServiceClient
        const trendingResponse = await challengeServiceClient.get('/challenge/get-trending', {
            params: {
                limit,
                last_evaluated_key: serializedKey,
            },
        });
        return {
            challenges: z.array(ChallengeSchema).parse(trendingResponse.data.challenges),
            userProfiles: [],
            last_evaluated_key: trendingResponse.data.last_evaluated_key || null,
            user_likes: trendingResponse.data.user_likes || [],
        };
    }

    static async addView(challenge_id: string, parent_type = 'CHALLENGE'): Promise<boolean> {
        const resp = await challengeServiceClient.post('/challenge/add-view', {
            parent_id: challenge_id,
            parent_type,
        });

        if (!resp) {
            console.error('Failed to add view to challenge');
            return false;
        }

        return resp.data.response;
    }

    // static async addView(challenge_id: string, parent_type = 'CHALLENGE') {
    //     try {
    //         const resp = await challengeServiceClient.post('/challenge/add-view', {
    //             parent_id: challenge_id,
    //             parent_type: parent_type,
    //         });
    //         if (!resp) {
    //             console.error('Failed to add view to challenge');
    //             return false;
    //         }

    //         return resp.data.response;
    //     } catch (error) {
    //         console.error('Error adding view:', error);
    //         return false;
    //     }
    // }

    static async getJudgesForChallenge(
        challenge_id: string,
        limit = 1000,
        last_evaluated_key: any = null
    ) {
        // Serialize last_evaluated_key if it's not null
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        // Make the GET request using challengeServiceClient
        const response = await challengeServiceClient.get('/challenge/get-judges-for-challenge', {
            params: {
                challenge_id,
                limit,
                last_evaluated_key: serializedKey,
            },
        });
        console.log('getJudgesForChallenge response', response.data);
        return {
            judges: response.data.judges,
            user_profiles: response.data.profiles,
            last_evaluated_key: response.data.last_evaluated_key || null,
        };
    }

    static async isSubscribed(
        target_id: string,
        target_type: string
    ): Promise<{ PERMANENT: boolean; TEMPORARY: boolean }> {
        if (!ensureAuthTokens()) {
            console.warn('No access token available.');
            return { PERMANENT: false, TEMPORARY: false };
        }

        const resp = await challengeServiceClient.get('/challenge/is-subscribed', {
            params: { target_id, target_type },
        });

        console.log('isSubscribed resp.data', resp.data);
        return resp.data;
    }

    // static async isSubscribed(
    //     target_id: string,
    //     target_type: string
    // ): Promise<{ PERMANENT: boolean; TEMPORARY: boolean }> {
    //     if (!ensureAuthTokens()) {
    //         console.warn('No access token available.');
    //         return { PERMANENT: false, TEMPORARY: false };
    //     }
    //     try {
    //         const resp = await challengeServiceClient.get('/challenge/is-subscribed', {
    //             params: {
    //                 target_id,
    //                 target_type,
    //             },
    //         });
    //         console.log('isSubscribed resp.data', resp.data);
    //         return resp.data;
    //     } catch (error) {
    //         console.error('Error checking subscription:', error);
    //         return { PERMANENT: false, TEMPORARY: false };
    //     }
    // }

    // static async subscribe(
    //     target_ids: string[],
    //     target_type: string,
    //     sub_type: string
    // ): Promise<boolean> {
    //     if (!ensureAuthTokens()) {
    //         console.warn('No access token available.');
    //         return false;
    //     }
    //     try {
    //         const resp = await challengeServiceClient.post('/challenge/subscribe', {
    //             target_ids,
    //             target_type,
    //             sub_type,
    //         });
    //         // const resp = await api.updateChallenge(accessToken, this);
    //         if (resp.status === 200 && resp.data.message === 'Subscription added successfully.') {
    //             return true;
    //         } else {
    //             console.error('Failed to subscribe:', resp.data.error);
    //             return false;
    //         }
    //     } catch (error: any) {
    //         console.error('Error subscribing:', error);
    //         return false;
    //     }
    // }

    static async subscribe(
        target_ids: string[],
        target_type: string,
        sub_type: string
    ): Promise<boolean> {
        if (!ensureAuthTokens()) {
            console.warn('No access token available.');
            return false;
        }

        const resp = await challengeServiceClient.post('/challenge/subscribe', {
            target_ids,
            target_type,
            sub_type,
        });

        return resp.status === 200 && resp.data.message === 'Subscription added successfully.';
    }

    static async unsubscribe(
        target_id: string,
        target_type: string,
        sub_type: string
    ): Promise<boolean> {
        if (!ensureAuthTokens()) {
            console.warn('No access token available.');
            return false;
        }

        const resp = await challengeServiceClient.post('/challenge/unsubscribe', {
            target_id,
            target_type,
            sub_type,
        });

        return resp.status === 200 && resp.data.message === 'Subscription removed successfully.';
    }

    // static async unsubscribe(
    //     target_id: string,
    //     target_type: string,
    //     sub_type: string
    // ): Promise<boolean> {
    //     if (!ensureAuthTokens()) {
    //         console.warn('No access token available.');
    //         // duellyEventEmitter.emit('show-login-overlay');
    //         return false;
    //     }
    //     try {
    //         const resp = await challengeServiceClient.post('/challenge/unsubscribe', {
    //             target_id,
    //             target_type,
    //             sub_type,
    //         });
    //         // const resp = await api.updateChallenge(accessToken, this);
    //         if (resp.status === 200 && resp.data.message === 'Subscription removed successfully.') {
    //             return true;
    //         } else {
    //             console.error('Failed to Unsubscribe:', resp.data.error);
    //             return false;
    //         }
    //     } catch (error) {
    //         console.error('Error Unsubscribing:', error);
    //         return false;
    //     }
    // }

    static async feedback(form_data: object): Promise<boolean> {
        console.log('feedback form_data', form_data);

        const resp = await challengeServiceClient.post('/challenge/feedback', form_data);
        return resp.status === 200 && resp.data.message === 'Feedback successfully.';
    }

    // static async feedback(form_data: object): Promise<boolean> {
    //     console.log('feedback form_data', form_data);
    //     try {
    //         const resp = await challengeServiceClient.post('/challenge/feedback', form_data);
    //         console.log('feedback resp', resp);
    //         if (resp.status === 200 && resp.data.message === 'Feedback successfully.') {
    //             return true;
    //         } else {
    //             console.error('Feedback Failed:', resp.data.error);
    //             return false;
    //         }
    //     } catch (error) {
    //         console.error('Error Leaving Feedback:', error);
    //         return false;
    //     }
    // }

    static async version(): Promise<string | null> {
        const resp = await challengeServiceClient.get('/challenge/get-version');
        return resp.status === 200 && resp.data.version ? resp.data.version : null;
    }

    // static async version(): Promise<string> {
    //     try {
    //         const resp = await challengeServiceClient.get('/challenge/get-version');
    //         console.log('feedback resp', resp);
    //         if (resp.status === 200 && resp.data.version) {
    //             return resp.data.version;
    //         } else {
    //             throw new Error('Failed to get version');
    //         }
    //     } catch (error) {
    //         throw new Error('Failed to get version');
    //     }
    // }

    static async userCanRespondToChallenge(
        challenge_id: string
    ): Promise<{ status: boolean; message: string }> {
        if (!ensureAuthTokens()) {
            console.warn('No access token available.');
            // duellyEventEmitter.emit('show-login-overlay');
            return {
                status: false,
                message: 'No access token available.',
            };
        }
        const resp = await challengeServiceClient.get('/challenge/user-can-respond-to-challenge', {
            params: { challenge_id },
        });

        return {
            status: resp.data?.status || false,
            message: resp.data?.message || 'User is eligible to respond',
        };
    }
}

export default ChallengeService;
